<template>
  <div class="bundle-detail-container">
    <el-main>
      <el-card class="bundle-info-card">
        <div v-if="loading" class="loading-container">
          <el-skeleton animated>
            <template>
              <div class="bundle-header">
                <div class="collections-preview">
                  <el-skeleton-item variant="image" style="width: 140px; height: 180px"/>
                </div>
                <div style="flex: 1">
                  <el-skeleton-item variant="h1" style="width: 50%"/>
                  <el-skeleton-item variant="text" style="margin-top: 16px; width: 30%"/>
                  <el-skeleton-item variant="text" style="margin-top: 16px"/>
                </div>
              </div>
            </template>
          </el-skeleton>
        </div>

        <template v-else>
          <div class="bundle-header">
            <div class="bundle-info">
              <div class="title-row">
                <h2>{{ bundle.bundle_name }}</h2>
                <div class="actions" v-if="isOwner">
                  <el-button 
                    type="primary" 
                    icon="el-icon-edit"
                    @click="handleEdit"
                  >
                    Edit
                  </el-button>
                  <el-button 
                    type="danger" 
                    icon="el-icon-delete"
                    @click="handleDelete"
                  >
                    Delete
                  </el-button>
                </div>
              </div>

              <div class="author">
                <span>By</span>
                <el-avatar 
                  :size="24" 
                  :src="bundle.user_avatar_address_small"
                  :style="{
                    background: bundle.user_avatar_address_small ? '#ffffff' : '#f0f2f5',
                    border: bundle.user_avatar_address_small ? '1px solid #f0f2f5' : 'none'
                  }"
                  style="margin: 0 8px"
                >
                  {{ bundle.user_name?.charAt(0) }}
                </el-avatar>
                <span class="creator-link">{{ bundle.user_name }}</span>
                <span style="margin-left: 8px;"> | Last updated: {{ formatDate(bundle.update_time * 1000) }}</span>
              </div>

              <p class="description">{{ bundle.bundle_description }}</p>

              <div class="bundle-stats">
                <div class="stat-item">
                  <i class="el-icon-folder-opened"></i>
                  <span>{{ collectionStats.groupCount }} Groups</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-notebook-2"></i>
                  <span>{{ collectionStats.knowitCount }} Units</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-document"></i>
                  <span>{{ collectionStats.contentCount }} Contents</span>
                </div>
                <div class="stat-item">
                  <i class="el-icon-question"></i>
                  <span>{{ collectionStats.quizCount }} Quizzes</span>
                </div>
              </div>

              <div class="bundle-meta">
                <div class="price-tag" v-if="hasCustomPrice">
                  <span class="original-price">${{ totalOriginalPrice }}</span>
                  <span class="knowit-bundle-price">${{ bundle.bundle_usd_price }}</span>
                  <span class="bundle-price">
                    <template v-if="bundle.custom_price === 0">FREE</template>
                    <template v-else>${{ bundle.custom_price }}</template>
                  </span>
                  <span class="discount-tag">{{ customDiscountVsCollections }}% OFF vs Individual</span>
                  <span class="discount-tag savings-tag" v-if="customDiscountVsBundle > 0">{{ customDiscountVsBundle }}% OFF vs Bundle Price</span>
                </div>
                <div class="price-tag" v-else>
                  <span class="original-price">${{ totalOriginalPrice }}</span>
                  <span class="bundle-price">
                    <template v-if="bundle.bundle_usd_price === 0">
                      FREE
                    </template>
                    <template v-else>
                      ${{ bundle.bundle_usd_price }}
                    </template>
                  </span>
                  <span class="discount-tag">{{ discountPercentage }}% OFF</span>
                </div>
              </div>

              <div class="action-row" v-if="!isOwner && userType === 'advisor'">
                <el-button
                  type="primary"
                  size="large"
                  @click="getCopiesForStudents"
                >
                  Purchase for My Students
                </el-button>
                <el-button
                  type="primary"
                  @click="shareToStudents"
                >
                  Share to My Students
                  <el-tooltip content="Share free access link with your students" placement="top">
                    <i class="el-icon-info" style="margin-left: 5px"></i>
                  </el-tooltip>
                </el-button>
                <el-button
                  type="primary"
                  plain
                  @click="viewOrders"
                >
                  View Orders
                </el-button>
              </div>
            </div>
          </div>

          <div class="contents-section">
            <div class="bundle-stats">
              <h3>Collections in this Bundle</h3>
              <div class="stat-item">
                <i class="el-icon-folder"></i>
                <span>{{ Object.keys(bundle.collection_array || {}).length }} Collections</span>
              </div>
            </div>
            <div class="contents-grid">
              <collection-card
                v-for="collection in Object.values(bundle.collection_array || {})"
                :key="collection.collection_id.$id"
                :collection="collection"
                @click.native="viewContent(collection)"
              />
            </div>
          </div>
        </template>
      </el-card>
    </el-main>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId,getUserType } from '@/utils/store'
import CollectionCard from '@/components/CollectionCard.vue'

export default {
  name: 'BundleDetail',
  components: {
    CollectionCard
  },
  data() {
    return {
      bundle: {},
      loading: true,
      isPurchased: false,
      userType: getUserType()
    }
  },
  computed: {
    isOwner() {
      return this.bundle.user_id === getUserId()
    },
    hasCustomPrice() {
      return this.bundle.custom_price !== undefined
        && this.bundle.custom_price !== null
        && Number(this.bundle.custom_price) < Number(this.bundle.bundle_usd_price);
    },
    effectiveBundlePrice() {
      return this.hasCustomPrice ? Number(this.bundle.custom_price) : Number(this.bundle.bundle_usd_price);
    },
    customDiscountVsCollections() {
      const originalPrice = Number(this.totalOriginalPrice);
      const customPrice = Number(this.bundle.custom_price || 0);
      if (originalPrice === 0) return 0;
      return Math.round(((originalPrice - customPrice) / originalPrice) * 100);
    },
    customDiscountVsBundle() {
      const bundlePrice = Number(this.bundle.bundle_usd_price || 0);
      const customPrice = Number(this.bundle.custom_price || 0);
      if (bundlePrice === 0) return 0;
      return Math.round(((bundlePrice - customPrice) / bundlePrice) * 100);
    },
    discountPercentage() {
      const originalPrice = Number(this.totalOriginalPrice);
      const bundlePrice = Number(this.bundle.bundle_usd_price || 0);
      if (originalPrice === 0) return 0;
      return Math.round(((originalPrice - bundlePrice) / originalPrice) * 100);
    },
    collectionStats() {
      const collections = Object.values(this.bundle.collection_array || {});
      return collections.reduce((acc, collection) => {
        acc.groupCount += collection.group_count || 0;
        acc.knowitCount += collection.knowit_count || 0;
        acc.contentCount += collection.content_count || 0;
        acc.quizCount += collection.quiz_count || 0;
        return acc;
      }, {
        groupCount: 0,
        knowitCount: 0,
        contentCount: 0,
        quizCount: 0
      });
    },
    totalOriginalPrice() {
      const collections = Object.values(this.bundle.collection_array || {});
      const total = collections.reduce((total, collection) => {
        return total + (parseFloat(collection.usd_price) || 0);
      }, 0);
      return total.toFixed(2);
    }
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleDateString()
    },
    async fetchBundleDetail() {
      this.loading = true
      
      try {
        const response = await postRequest('getCollectionBundle', {
          bundle_id: this.$route.query.bundle_id
        })
        
        if (response.data.code === 0) {
          this.bundle = response.data.data
          this.checkPurchaseStatus()
        }
      } catch (error) {
        console.error('Error fetching bundle details:', error)
        this.$message.error('Failed to fetch bundle details')
      } finally {
        this.loading = false
      }
    },
    async checkPurchaseStatus() {
      try {
        const response = await postRequest('checkBundlePurchase', {
          bundle_id: this.$route.query.id,
          user_id: getUserId()
        })
        this.isPurchased = response.data.code === 0 && response.data.data.purchased
      } catch (error) {
        console.error('Error checking purchase status:', error)
      }
    },
    handleEdit() {
      this.$router.push({
        name: 'Edit Bundle',
        query: { id: this.bundle._id.$id }
      })
    },
    async handleDelete() {
      try {
        await this.$confirm('Are you sure you want to delete this bundle?', 'Warning', {
          type: 'warning'
        })
        
        const response = await postRequest('deleteBundle', {
          bundle_id: this.bundle._id.$id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Bundle deleted successfully')
          this.$router.push('/home/findcontents')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting bundle:', error)
          this.$message.error('Failed to delete bundle')
        }
      }
    },
    viewContent(content) {
      this.$router.push({
        name: 'Content Profile',
        query: { 
          content_id: content.collection_id.$id,
          content_type: content.type
        }
      })
    },
    shareToStudents() {
      // Implement share functionality
    },
    getCopiesForStudents() {
      this.$router.push({
        name: 'Content Order Form',
        query: {
          content_id: this.bundle._id.$id,
          title: this.bundle.bundle_name,
          type: 'bundle',
          usd_price: this.effectiveBundlePrice,
        }
      })
    },
    viewOrders() {
      this.$router.push({
        name: 'Content Orders',
        query: {
          content_id: this.bundle._id.$id
        }
      })
    }
  },
  mounted() {
    this.fetchBundleDetail()
  }
}
</script>

<style lang="scss" scoped>
.bundle-detail-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 10px;
}

.bundle-header {
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
}

.bundle-info {
  flex: 1;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;

  h2 {
    margin: 0;
    font-size: 24px;
    color: #303133;
  }
}

.author {
  color: #606266;
  margin: 5px 0 15px;
  display: flex;
  align-items: center;
}

.description {
  font-size: 16px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 20px;
}

.bundle-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  align-items: baseline;
  line-height: 1;

  h3 {
    margin: 0;
    line-height: 1;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #606266;
    line-height: 1;
    height: 24px;

    i {
      font-size: 18px;
      color: #409EFF;
      line-height: 1;
    }

    span {
      line-height: 1;
    }
  }
}

.bundle-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .price-tag {
    display: flex;
    align-items: center;

    .original-price {
      text-decoration: line-through;
      color: #909399;
      margin-right: 8px;
      font-size: 18px;
    }

    .bundle-price {
      font-size: 28px;
      font-weight: bold;
      color: #303133;
    }

    .knowit-bundle-price {
      text-decoration: line-through;
      color: #909399;
      margin-right: 8px;
      font-size: 16px;
    }

    .discount-tag {
      background: #f56c6c;
      color: white;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 14px;
      margin: 0 4px;
    }

    .savings-tag {
      background: #67c23a;
    }
  }
}

.contents-section {
  margin-top: 40px;

  h3 {
    margin-bottom: 20px;
    color: #303133;
  }
}

.contents-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.creator-link {
  color: #606266;
}

.loading-container {
  min-height: 400px;
}
</style> 